import type { ExecutionDetail, ExecutionPage, ExecutionQuery } from '../types/executions'
import { fetchApi } from './api'

/**
 * Execution History — GET /api/observatory/executions, LIVE, read-only.
 *
 * Served by ObservatoryExecutionsController from Etl.EtlRun (365 days) and its
 * EtlEntityRun rows. Plain [Authorize], like the other ETL telemetry panels.
 *
 * EVERY FILTER IS APPLIED SERVER-SIDE, and must stay that way: the page is
 * capped at 100 runs, so a client filtering a page it already fetched would be
 * filtering a window of the answer and calling it the answer — and the
 * `stale`/`running` split must come from the server's one stale rule, never a
 * copy of it here.
 *
 * An out-of-range `top` or an unknown `status` is refused with a 400 rather
 * than clamped or ignored. The SPA only sends values from ExecutionQuery, so
 * that 400 is a wire-contract break rather than a user error.
 */

const BASE = '/api/observatory/executions'

export function getExecutions(query: ExecutionQuery = {}): Promise<ExecutionPage> {
  const params = new URLSearchParams()

  // Only set parameters that were actually asked for. An empty string is a value
  // to the model binder, not an absent filter.
  if (query.skip != null) params.set('skip', String(query.skip))
  if (query.top != null) params.set('top', String(query.top))
  if (query.status) params.set('status', query.status)
  if (query.module) params.set('module', query.module)
  if (query.entityName) params.set('entityName', query.entityName)
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)

  const qs = params.toString()
  return fetchApi<ExecutionPage>(`${BASE}${qs ? `?${qs}` : ''}`)
}

/** One run in full. A 404 means the id is unknown or past its 365-day retention. */
export function getExecution(runId: string): Promise<ExecutionDetail> {
  return fetchApi<ExecutionDetail>(`${BASE}/${encodeURIComponent(runId)}`)
}
